import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { IMG } from "@/lib/images";
import { ProductCard, RecipeCard, SectionHeader, ComplianceNote, StarRating, Wordmark } from "@/components/kit";
import { Wine, Gift, BookOpen, Users, CalendarDays, ShoppingBag, ArrowRight, Sparkles } from "lucide-react";

const OCCASIONS = [
  { name: "Weddings", img: IMG.wedding }, { name: "Housewarming", img: IMG.tropical },
  { name: "Fathers Day", img: IMG.oldfashioned }, { name: "Christmas", img: IMG.champagne },
];
const ECOSYSTEM = ["Amazon", "Drizly", "ReserveBar", "Cocktail Kingdom", "Total Wine"];

export default function Home() {
  const [recipes, setRecipes] = useState([]);
  const [products, setProducts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [feed, setFeed] = useState([]);
  useEffect(() => {
    api.get("/recipes").then((r) => setRecipes(r.data.slice(0, 4)));
    api.get("/products?sort=rating").then((r) => setProducts(r.data.slice(0, 4)));
    api.get("/groups").then((r) => setGroups(r.data));
    api.get("/feed").then((r) => setFeed(r.data.slice(0, 3)));
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <img src={IMG.heroBar} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/30 dark:from-background dark:via-background/80" />
        <div className="relative mx-auto max-w-6xl px-6 py-20 lg:py-28">
          <div className="max-w-2xl animate-fade-up">
            <p className="overline text-primary">Social · Discovery · Gifting</p>
            <h1 className="mt-4 font-display text-5xl leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
              For the right ingredients,<br />for the <span className="italic text-primary">right drinks.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-muted-foreground">
              Create the most original gift registry, discover premium drinks, build a bar to your budget, and share exactly what you love with friends and guests.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link data-testid="hero-build-bar" to="/build-bar" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground transition-transform hover:scale-[1.03]"><Wine size={18} /> Build My Bar</Link>
              <Link data-testid="hero-registry" to="/registry" className="inline-flex items-center gap-2 rounded-full bg-secondary px-6 py-3 font-semibold text-secondary-foreground transition-transform hover:scale-[1.03]"><Gift size={18} /> Create a Registry</Link>
              <Link data-testid="hero-recipes" to="/recipes" className="inline-flex items-center gap-2 rounded-full border border-border bg-card/70 px-6 py-3 font-semibold transition-colors hover:border-primary"><BookOpen size={18} /> Explore Recipes</Link>
            </div>
          </div>
        </div>
        {/* floating cards */}
        <div className="pointer-events-none absolute right-8 top-1/2 hidden -translate-y-1/2 flex-col gap-4 xl:flex">
          <FloatCard img={IMG.margarita} title="Classic Margarita" sub="★ 4.8 · Tequila" />
          <FloatCard img={IMG.amberhand} title="Don Julio Añejo" sub="Rated 4.6 by 1,284" />
          <FloatCard img={IMG.wedding} title="Emily & James Bar" sub="Wedding registry · 69%" />
        </div>
      </section>

      <div className="mx-auto max-w-6xl space-y-20 px-6 py-16">
        {/* SOCIAL PROOF */}
        <section>
          <SectionHeader overline="What friends are drinking" title="Your feed is pouring" action={<Link to="/feed" className="hidden text-sm font-semibold text-primary hover-underline sm:flex items-center gap-1">Open feed <ArrowRight size={14} /></Link>} />
          <div className="grid gap-6 md:grid-cols-3">
            {feed.map((p) => (
              <div key={p.id} className="card-lift overflow-hidden rounded-xl border border-border bg-card">
                <img src={p.image} alt="" className="h-40 w-full object-cover" />
                <div className="p-4">
                  <div className="flex items-center gap-2"><img src={p.avatar} className="h-7 w-7 rounded-full object-cover" alt="" /><span className="text-sm font-semibold">{p.author}</span><span className="text-xs text-muted-foreground">· {p.time}</span></div>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FEATURED RECIPES */}
        <section>
          <SectionHeader overline="Pinterest-style discovery" title="Featured recipes" action={<Link to="/recipes" className="text-sm font-semibold text-primary hover-underline">All recipes</Link>} />
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">{recipes.map((r) => <RecipeCard key={r.id} r={r} />)}</div>
        </section>

        {/* TOP RATED */}
        <section>
          <SectionHeader overline="Community favorites" title="Top-rated bottles" action={<Link to="/ratings" className="text-sm font-semibold text-primary hover-underline">Browse ratings</Link>} />
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">{products.map((p) => <ProductCard key={p.id} p={p} />)}</div>
        </section>

        {/* BAR BUILDER EXPLAINER */}
        <section className="overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-secondary/10 via-card to-primary/10">
          <div className="grid items-center gap-8 p-8 lg:grid-cols-2 lg:p-12">
            <div>
              <p className="overline text-primary">Budget bar builder</p>
              <h2 className="mt-3 font-display text-4xl leading-tight">Pick your drinks, we'll provide the ingredients.</h2>
              <p className="mt-4 text-muted-foreground">Set an occasion, guest count and budget. Our concierge builds a complete bar — bottles, mixers, garnishes, tools and glassware — ready for delivery or local pickup through our partners.</p>
              <ul className="mt-5 space-y-2 text-sm">
                {["Choose budget, balanced or premium", "AI-recommended bottles for your occasion", "One-tap shopping through partners"].map((t) => (
                  <li key={t} className="flex items-center gap-2"><Sparkles size={15} className="text-primary" /> {t}</li>
                ))}
              </ul>
              <Link data-testid="explainer-build" to="/build-bar" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground">Start Build My Bar <ArrowRight size={16} /></Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img src={IMG.bar2} alt="" className="col-span-2 h-48 w-full rounded-xl object-cover" />
              <img src={IMG.coupe} alt="" className="h-32 w-full rounded-xl object-cover" />
              <img src={IMG.shelves} alt="" className="h-32 w-full rounded-xl object-cover" />
            </div>
          </div>
        </section>

        {/* REGISTRY OCCASIONS */}
        <section>
          <SectionHeader overline="The most original gift registry" title="Registry occasions" action={<Link to="/registry" className="text-sm font-semibold text-primary hover-underline">Explore registries</Link>} />
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
            {OCCASIONS.map((o) => (
              <Link to="/registry" key={o.name} className="card-lift group relative aspect-[4/5] overflow-hidden rounded-xl">
                <img src={o.img} alt={o.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <span className="absolute bottom-4 left-4 font-display text-2xl text-white">{o.name}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* PLANNING + GROUPS */}
        <section className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-8">
            <CalendarDays className="text-primary" />
            <h3 className="mt-4 font-display text-3xl">Party planning tools</h3>
            <p className="mt-2 text-muted-foreground">Guest calculators, drink quantity estimators, printable checklists and themed bar setups for every celebration.</p>
            <Link to="/planner" className="mt-4 inline-flex items-center gap-1 font-semibold text-primary hover-underline">Open the planner <ArrowRight size={14} /></Link>
          </div>
          <div className="rounded-2xl border border-border bg-card p-8">
            <Users className="text-primary" />
            <h3 className="mt-4 font-display text-3xl">Interest groups</h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {groups.map((g) => <Link key={g.id} to={`/groups/${g.id}`} className="rounded-full border border-border px-3 py-1.5 text-sm hover:border-primary hover:text-primary">{g.name}</Link>)}
            </div>
          </div>
        </section>

        {/* AFFILIATE ECOSYSTEM */}
        <section className="rounded-2xl border border-border bg-card p-8 text-center">
          <ShoppingBag className="mx-auto text-primary" />
          <h3 className="mt-3 font-display text-3xl">A shopping ecosystem built on trust</h3>
          <p className="mx-auto mt-2 max-w-xl text-muted-foreground">Shop ingredients, bottles and tools through vetted partners with price comparisons and availability labels.</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-muted-foreground">
            {ECOSYSTEM.map((e) => <span key={e} className="font-display text-2xl">{e}</span>)}
          </div>
        </section>
      </div>

      {/* FOOTER */}
      <footer className="border-t border-border bg-card/40">
        <div className="mx-auto grid max-w-6xl gap-8 px-6 py-12 sm:grid-cols-3">
          <div><Wordmark /><p className="mt-3 text-sm text-muted-foreground">The social home for spirits, wine, beer, cocktails and home bar culture.</p></div>
          <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground sm:col-span-2 sm:justify-items-end">
            <Link to="/recipes" className="hover:text-primary">Recipes</Link><Link to="/ratings" className="hover:text-primary">Ratings</Link>
            <Link to="/registry" className="hover:text-primary">Registry</Link><Link to="/build-bar" className="hover:text-primary">Build My Bar</Link>
            <Link to="/groups" className="hover:text-primary">Groups</Link><Link to="/planner" className="hover:text-primary">Planner</Link>
          </div>
        </div>
        <div className="border-t border-border px-6 py-4"><div className="mx-auto max-w-6xl"><ComplianceNote>Please drink responsibly. You must be 21+ to use StockMyBar. Alcohol sales are fulfilled by licensed partners; availability, delivery and pickup vary by partner and local regulations.</ComplianceNote></div></div>
      </footer>
    </div>
  );
}

function FloatCard({ img, title, sub }) {
  return (
    <div className="w-56 overflow-hidden rounded-xl glass border border-border shadow-xl animate-fade-up">
      <div className="flex items-center gap-3 p-3">
        <img src={img} alt="" className="h-12 w-12 rounded-lg object-cover" />
        <div><p className="text-sm font-semibold leading-tight">{title}</p><p className="text-xs text-muted-foreground">{sub}</p></div>
      </div>
    </div>
  );
}
